// ============================================
// MENUPERFIL.JS — Dropdown de perfil de usuario
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { solicitarPermiso } from '../utils/notificaciones';
import { useTema } from '../utils/tema';
import TipoCambioWidget from './TipoCambioWidget';
import { toggleDiagScroll, isDiagOn } from '../utils/diagScroll';
import { URL_APP } from '../config/api';
import { estadoInstalacion, instalarNativo, suscribirInstalacion, plataforma } from '../utils/instalacion';

// Version = hash del bundle principal (unico por despliegue). Sirve de "numero de
// version" visible y como destino de los 5 toques que encienden el diagnostico.
function versionBundle() {
  try {
    const sc = document.querySelector('script[src*="/static/js/main."]');
    const m = sc && sc.src.match(/main\.([a-f0-9]+)\.js/);
    return m ? m[1] : 'dev';
  } catch { return 'dev'; }
}

const PP = "'Poppins', sans-serif";
const colores = { principal: '#5c9adb', oscuro: '#2f5f9e', gris: '#4e5357', grisClaro: '#5e666e', rojo: '#e05c5c', fondo: '#f3f4f4' };

const rolesCfg = {
  'Administrador': { bg: '#e0e8f8', color: '#1a3a6b' },
  'Dirección': { bg: '#e6f1fb', color: '#1a5fa8' },
  'Comercial': { bg: '#fffbe6', color: '#7a6e00' },
  'Supervisor': { bg: '#e8faf5', color: '#1D9E75' },
  'Técnico': { bg: '#fff5ee', color: '#9a3412' },
  'Almacén': { bg: '#f0fdf4', color: '#15803d' },
  'Facturación': { bg: '#d1fae5', color: '#065f46' },
};

// ============================================
// DIBUJOS DE LOS PASOS DE INSTALACION (INSTALAR-APP, 2026-09-14)
// ============================================
// No son adorno. En iOS, Apple NO permite el boton de instalar: la unica via es que la persona
// encuentre "Añadir a pantalla de inicio" dentro del menu Compartir, y NADIE lo encuentra si no
// reconoce el icono. Por eso cada paso lleva dibujado el boton real que hay que tocar.

// El icono Compartir de iOS: cuadro abierto por arriba con una flecha saliendo.
const IconoCompartirIOS = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a84ff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v12" />
    <path d="M8 7l4-4 4 4" />
    <path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" />
  </svg>
);

// Los 3 puntos verticales del menu de Chrome en Android.
const IconoTresPuntos = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#4b5563" aria-hidden="true">
    <circle cx="12" cy="5" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="12" cy="19" r="1.9" />
  </svg>
);

// "Añadir a pantalla de inicio": un cuadrado con un mas dentro.
const IconoAnadir = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2f5f9e" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
    <path d="M12 8.5v7M8.5 12h7" />
  </svg>
);

// La confirmacion final: una paloma.
const IconoListo = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12.5l5 5L20 6.5" />
  </svg>
);

// La pantalla de inicio del telefono, con el icono ya puesto.
const IconoPantallaInicio = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.6" aria-hidden="true">
    <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
    <rect x="9" y="6" width="6" height="6" rx="1.6" fill="#2f5f9e" stroke="none" />
    <path d="M10 19h4" strokeLinecap="round" />
  </svg>
);

const PASOS_IOS = {
  titulo: 'iPhone y iPad (Safari)',
  pasos: [
    { dibujo: <IconoCompartirIOS />, texto: 'Abrela en Safari y toca el boton Compartir, abajo en el centro' },
    { dibujo: <IconoAnadir />, texto: 'Baja y elige "Añadir a pantalla de inicio"' },
    { dibujo: <IconoListo />, texto: 'Toca "Añadir", arriba a la derecha' },
    { dibujo: <IconoPantallaInicio />, texto: 'Listo: el icono de Beep queda en tu pantalla de inicio' },
  ],
};

const PASOS_ANDROID = {
  titulo: 'Android (Chrome)',
  pasos: [
    { dibujo: <IconoTresPuntos />, texto: 'Toca los 3 puntos, arriba a la derecha' },
    { dibujo: <IconoAnadir />, texto: 'Elige "Instalar aplicacion" o "Añadir a pantalla de inicio"' },
    { dibujo: <IconoListo />, texto: 'Confirma con "Instalar"' },
  ],
};

function MenuPerfil({ abierto, onCerrar, nombre, correo, rol, esCelular, onMiPerfil, onCambiarPassword, onCerrarSesion }) {
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [notifMsg, setNotifMsg] = useState('');
  const { temaId, setTemaId } = useTema();
  const [mostrarInstrucciones, setMostrarInstrucciones] = useState(false);
  // El estado de instalacion lo sabe utils/instalacion.js, que captura `beforeinstallprompt` en
  // cuanto carga el modulo. Aqui solo se escucha para repintar cuando cambie.
  const [estadoInst, setEstadoInst] = useState(estadoInstalacion);
  const [secAbierta, setSecAbierta] = useState({ cuenta: true, preferencias: false, soporte: false });

  const toggleSec = (k) => setSecAbierta(s => ({ ...s, [k]: !s[k] }));

  // 5 toques seguidos en la version encienden/apagan el diagnostico de saltos de scroll.
  const tapsRef = useRef({ n: 0, t: 0 });
  const [diagOn, setDiagOn] = useState(isDiagOn());
  const [tapHint, setTapHint] = useState('');
  const onTapVersion = () => {
    const now = Date.now();
    const r = tapsRef.current;
    if (now - r.t > 1500) r.n = 0;   // reinicia si pasan mas de 1.5s entre toques
    r.t = now; r.n += 1;
    if (r.n >= 5) {
      r.n = 0;
      const nuevo = toggleDiagScroll();
      setDiagOn(nuevo);
      setTapHint(nuevo ? 'Diagnostico ENCENDIDO (aparece un boton abajo)' : 'Diagnostico apagado');
      setTimeout(() => setTapHint(''), 3000);
    } else if (r.n >= 3) {
      setTapHint((5 - r.n) + ' toques mas para el modo diagnostico');
      setTimeout(() => setTapHint(''), 1200);
    }
  };

  // Antes esto capturaba `beforeinstallprompt` aqui dentro, pero el navegador lo dispara UNA vez y
  // temprano: cuando este menu se montaba, el evento ya habia pasado y el boton no servia. Ahora lo
  // guarda el modulo al cargar y este componente solo se entera de los cambios.
  useEffect(() => suscribirInstalacion(() => setEstadoInst(estadoInstalacion())), []);

  // La barra de "instala la app" no puede lanzar nada nativo en iOS, asi que pide que se abran
  // ESTAS instrucciones. Se escucha siempre, tambien con el menu cerrado: la hoja se pinta fuera
  // del menu y tiene que poder abrirse desde cualquier pantalla.
  useEffect(() => {
    const abrir = () => setMostrarInstrucciones(true);
    window.addEventListener('beep:abrir-instrucciones-instalar', abrir);
    return () => window.removeEventListener('beep:abrir-instrucciones-instalar', abrir);
  }, []);

  // Ojo: no basta con `if (!abierto) return null`. La hoja de instrucciones se abre tambien
  // desde la barra de instalacion, con el menu CERRADO; si se devolviera null nunca se veria.
  if (!abierto && !mostrarInstrucciones) return null;

  const cfg = rolesCfg[rol] || { bg: '#f3f4f4', color: '#5e666e' };
  const iniciales = (nombre || 'U').split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();

  const handleNotif = async () => {
    const res = await solicitarPermiso();
    if (res === 'granted') setNotifMsg('Notificaciones activadas');
    else if (res === 'denied') setNotifMsg('Permiso denegado. Actívalas en configuración del navegador.');
    else setNotifMsg('Permiso pendiente');
    setTimeout(() => setNotifMsg(''), 3000);
  };

  const handleLogout = () => {
    if (!confirmLogout) { setConfirmLogout(true); return; }
    if (onCerrarSesion) onCerrarSesion();
  };

  const instalarApp = async () => {
    if (estadoInstalacion() === 'boton') {
      await instalarNativo();
      setEstadoInst(estadoInstalacion());
    } else {
      // iOS nunca da prompt nativo (Apple no lo permite): ahi la unica via son las instrucciones.
      setMostrarInstrucciones(true);
    }
  };

  // El texto cambia con el aparato: en escritorio no se dice "aplicacion" a secas, se dice donde.
  const textoInstalar = plataforma() === 'escritorio' ? 'Instalar en este equipo' : 'Instalar aplicacion';

  const menuStyle = esCelular ? {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: '#fff', zIndex: 10001,
    display: 'flex', flexDirection: 'column',
    animation: 'menuPerfilIn 0.25s ease-out forwards',
  } : {
    position: 'absolute', top: '100%', right: 0, marginTop: '8px',
    width: '280px', background: '#fff', borderRadius: '16px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.15)', border: '1px solid #e8e8e8',
    zIndex: 10001, overflow: 'hidden',
    maxHeight: 'calc(100vh - 80px)',
    display: 'flex', flexDirection: 'column',
    animation: 'menuPerfilIn 0.2s ease-out forwards',
  };

  const itemStyle = { padding: '10px 20px', cursor: 'pointer', fontSize: '13px', color: colores.gris, display: 'flex', alignItems: 'center', gap: '10px', transition: 'background 0.1s', fontFamily: PP };
  const secHeader = { padding: '12px 20px', fontSize: '10px', fontWeight: '600', color: colores.grisClaro, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' };

  return (
    <>
      <style>{`
        @keyframes menuPerfilIn {
          0% { opacity: 0; transform: translateY(-8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {abierto && (<>
      {/* Overlay */}
      {!esCelular && <div onClick={onCerrar} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000 }} />}

      <div style={menuStyle}>
        {/* Header con cerrar en celular */}
        {esCelular && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px 0' }}>
            <div onClick={onCerrar} style={{ width: '32px', height: '32px', borderRadius: '50%', background: colores.fondo, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '14px', color: colores.grisClaro }}>{'✕'}</div>
          </div>
        )}

        {/* Avatar + info */}
        <div style={{ padding: '20px 20px 16px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: cfg.bg, border: `2px solid ${cfg.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
            <span style={{ fontSize: '20px', fontWeight: '700', color: cfg.color, fontFamily: PP }}>{iniciales}</span>
          </div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: colores.gris, fontFamily: PP }}>{nombre}</div>
          <div style={{ fontSize: '12px', color: colores.grisClaro, marginTop: '2px', fontFamily: PP }}>{correo}</div>
          <span style={{ display: 'inline-block', marginTop: '8px', padding: '3px 12px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', background: cfg.bg, color: cfg.color, fontFamily: PP }}>{rol}</span>
        </div>

        {/* Tipo de cambio USD/MXN — siempre visible arriba del scroll */}
        <TipoCambioWidget />

        {/* Contenedor scrollable con acordeones */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {/* Mi cuenta */}
          <div onClick={() => toggleSec('cuenta')} style={secHeader}>
            <span>Mi cuenta</span>
            <span style={{ fontSize: '10px' }}>{secAbierta.cuenta ? '▼' : '▶'}</span>
          </div>
          {secAbierta.cuenta && (
            <>
              <div onClick={() => { onCerrar(); if (onMiPerfil) onMiPerfil(); }} style={itemStyle}>
                <span style={{ fontSize: '16px' }}>{'👤'}</span> Mi perfil
              </div>
              {/* INSTALAR-APP: va ARRIBA de Notificaciones, que es donde la gente la va a buscar.
                  Tres estados: ya instalada (gris, sin accion), se puede instalar (boton), o no hay
                  prompt nativo y toca explicarlo a mano (iOS y algun navegador suelto). */}
              {estadoInst === 'instalada' ? (
                <div style={{ ...itemStyle, color: '#9aa1a9', cursor: 'default' }}>
                  <span style={{ fontSize: '16px', opacity: 0.55 }}>{'📱'}</span> Aplicacion instalada
                </div>
              ) : (
                <div onClick={instalarApp} style={itemStyle}>
                  <span style={{ fontSize: '16px' }}>{'📱'}</span> {textoInstalar}
                </div>
              )}
              <div style={itemStyle}>
                <span style={{ fontSize: '16px' }}>{'🔔'}</span> Notificaciones
              </div>
              <div onClick={() => { onCerrar(); if (onCambiarPassword) onCambiarPassword(); }} style={itemStyle}>
                <span style={{ fontSize: '16px' }}>{'🔒'}</span> Cambiar mi contraseña
              </div>
            </>
          )}

          <div style={{ height: '1px', background: '#f0f0f0', margin: '4px 0' }} />

          {/* Preferencias */}
          <div onClick={() => toggleSec('preferencias')} style={secHeader}>
            <span>Preferencias</span>
            <span style={{ fontSize: '10px' }}>{secAbierta.preferencias ? '▼' : '▶'}</span>
          </div>
          {secAbierta.preferencias && (
            <>
              {navigator.share && (
                <div onClick={() => { navigator.share({ title: 'Beep OS', text: '¡Mira el sistema operativo de Beep México!', url: URL_APP }); }} style={itemStyle}>
                  <span style={{ fontSize: '16px' }}>{'📤'}</span> Compartir app
                </div>
              )}
              <div onClick={() => setTemaId(temaId === 'claro' ? 'oscuro' : 'claro')} style={itemStyle}>
                <span style={{ fontSize: '16px' }}>{temaId === 'claro' ? '☀️' : '🌙'}</span> {temaId === 'claro' ? 'Tema claro' : 'Tema oscuro'}
                <div style={{ marginLeft: 'auto', width: '36px', height: '20px', borderRadius: '10px', background: temaId === 'oscuro' ? '#5c9adb' : '#d0d0d0', position: 'relative', flexShrink: 0 }}>
                  <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: temaId === 'oscuro' ? '18px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
              </div>
              <div onClick={handleNotif} style={itemStyle}>
                <span style={{ fontSize: '16px' }}>{'🔔'}</span> Activar notificaciones push
              </div>
              {notifMsg && (
                <div style={{ padding: '6px 20px', fontSize: '11px', color: '#1D9E75', fontFamily: PP }}>{notifMsg}</div>
              )}
            </>
          )}

          <div style={{ height: '1px', background: '#f0f0f0', margin: '4px 0' }} />

          {/* Soporte */}
          <div onClick={() => toggleSec('soporte')} style={secHeader}>
            <span>Soporte</span>
            <span style={{ fontSize: '10px' }}>{secAbierta.soporte ? '▼' : '▶'}</span>
          </div>
          {secAbierta.soporte && (
            <>
              <div onClick={() => alert('Próximamente disponible')} style={itemStyle}>
                <span style={{ fontSize: '16px' }}>{'❓'}</span> Ayuda y tutoriales
              </div>
              <div onClick={() => { window.location.href = 'mailto:richerreraco@gmail.com?subject=Reporte%20Beep%20OS'; }} style={itemStyle}>
                <span style={{ fontSize: '16px' }}>{'💬'}</span> Reportar un problema
              </div>
              <div onClick={() => { onCerrar(); window.open('/privacidad', '_blank', 'noopener'); }} style={itemStyle}>
                <span style={{ fontSize: '16px' }}>{'🛡️'}</span> Aviso de privacidad
              </div>
              <div onClick={() => alert('Beep OS v1.0 — Sistema operativo de Beep México')} style={{ ...itemStyle, color: colores.grisClaro, fontSize: '12px' }}>
                <span style={{ fontSize: '16px' }}>{'📋'}</span> Beep OS v1.0
              </div>
            </>
          )}

          <div style={{ height: '1px', background: '#f0f0f0', margin: '4px 0' }} />

          {/* Nota sistema cerrado */}
          <div style={{ padding: '8px 20px', fontSize: '11px', color: colores.grisClaro, fontFamily: PP, lineHeight: 1.4 }}>
            ¿Necesitas dar acceso a alguien? Contacta al administrador del sistema.
          </div>
        </div>

        {/* Cerrar sesión — SIEMPRE visible al fondo, fuera del acordeón */}
        <div style={{ flexShrink: 0, borderTop: '1px solid #f0f0f0', background: '#fff' }}>
          {confirmLogout ? (
            <div style={{ padding: '14px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '13px', color: colores.gris, fontFamily: PP, marginBottom: '10px' }}>¿Estás seguro que deseas cerrar sesión?</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setConfirmLogout(false)} style={{ flex: 1, padding: '8px', background: colores.fondo, color: colores.grisClaro, border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: PP }}>Cancelar</button>
                <button onClick={handleLogout} style={{ flex: 1, padding: '8px', background: '#fff0f0', color: colores.rojo, border: `1px solid ${colores.rojo}`, borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: PP }}>Sí, cerrar sesión</button>
              </div>
            </div>
          ) : (
            <div onClick={handleLogout} style={{ ...itemStyle, color: colores.rojo, padding: '14px 20px' }}>
              <span style={{ fontSize: '16px' }}>{'🚪'}</span> Cerrar sesión
            </div>
          )}
        </div>

        {/* Pie: version del sitio. Tocarla 5 veces seguidas enciende/apaga el diagnostico
            EN VIVO de saltos de scroll (para depurar el bug de la ficha en el telefono). */}
        <div style={{ flexShrink: 0, padding: '10px 20px 14px', textAlign: 'center', background: '#fff' }}>
          <div
            onClick={onTapVersion}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: colores.grisClaro, fontFamily: PP, cursor: 'pointer', userSelect: 'none', WebkitTapHighlightColor: 'transparent', padding: '8px 14px', minHeight: '44px' }}
          >
            Beep OS · v{versionBundle()}{diagOn ? ' · 🐞 diag' : ''}
          </div>
          {tapHint && (
            <div style={{ fontSize: '11px', color: colores.oscuro, fontFamily: PP, marginTop: '2px' }}>{tapHint}</div>
          )}
        </div>

        {/* Modal instrucciones de instalación */}
      </div>
      </>)}

      {mostrarInstrucciones && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0,
            width: '100%', height: '100%',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center'
          }} onClick={() => setMostrarInstrucciones(false)}>
            <div style={{
              background: 'white',
              borderRadius: '20px 20px 0 0',
              padding: '24px',
              width: '100%',
              maxWidth: '480px',
              marginBottom: '0'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>{'📲'}</div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#2f5f9e' }}>
                  Instalar Beep OS
                </div>
                <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                  Sigue los pasos según tu dispositivo
                </div>
              </div>

              {/* INSTALAR-APP: pasos ILUSTRADOS, no solo texto. En iOS es la UNICA via — Apple no
                  permite el boton de instalar — y nadie encuentra "Añadir a pantalla de inicio" si
                  no reconoce el icono de Compartir. Se enseña primero la plataforma detectada. */}
              {(plataforma() === 'ios'
                ? [PASOS_IOS, PASOS_ANDROID]
                : [PASOS_ANDROID, PASOS_IOS]
              ).map((grupo, gi) => (
                <div key={grupo.titulo} style={{ marginBottom: gi === 0 ? '20px' : '24px' }}>
                  <div style={{
                    fontSize: '13px', fontWeight: '600', color: gi === 0 ? '#2f5f9e' : '#6b7280',
                    marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px',
                  }}>
                    {grupo.titulo}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {grupo.pasos.map((paso, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '26px', height: '26px', borderRadius: '50%',
                          background: gi === 0 ? '#2f5f9e' : '#9aa1a9', color: 'white',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '12px', fontWeight: '700', flexShrink: 0,
                        }}>{i + 1}</div>
                        {/* El dibujo del boton que hay que tocar, tal como se ve en el telefono */}
                        <div style={{
                          width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
                          background: '#f3f4f6', border: '1px solid #e5e7eb',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>{paso.dibujo}</div>
                        <div style={{ fontSize: '13px', color: '#444', lineHeight: 1.35 }}>{paso.texto}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <button onClick={() => setMostrarInstrucciones(false)} style={{
                width: '100%',
                padding: '14px',
                background: '#2f5f9e',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer'
              }}>
                Entendido
              </button>
            </div>
          </div>
        )}
    </>
  );
}

export default MenuPerfil;
